import { FormGroup } from '@components/molecules/form/FormGroup';
import { Modal } from '@components/molecules/Modal';
import { Input } from '@components/atoms/Input';
import { InputPhone } from '@components/atoms/InputPhone';
import { GoogleAddressInput } from '@components/atoms/GoogleAddressInput';
import ReactSelect from '@components/atoms/ReactSelect';
import { SelectOption } from '@components/atoms/ReactSelect';
import { useProjects } from '@hooks/api/projects/useProjects';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useCreateClient } from '@hooks/api/clients/useCreateClient';
import { useEditClient } from '@hooks/api/clients/useEditClient';
import { useRemoveClientAccess } from '@hooks/api/clients/useRemoveClientAccess';
import { Client } from '@models/client.model';
import { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { FileDropZone } from '@components/molecules/FileDropZone';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { useTranslation } from '@hooks/useTranslation';
import {
  isClientEmailAlreadyMemberError,
  isClientEmailAlreadyLinkedError,
} from '@utils/errorMessage';
import { formatDateOnly } from '@utils/formatDate';
import { ClientInviteNotice } from '@components/molecules/ClientInviteNotice';

type FormValues = {
  name: string;
  address: string;
  phone: string;
  email: string;
  projectIds: string[];
};

interface CreateEditClientModalProps {
  title: string;
  isLoading: boolean;
  showNewClientModal: boolean;
  initialData?: Client | null;
  toggleNewClientModal: () => void;
}

// Sprint 25, story-128 (run 5). Flow D: changing/removing the email of a Client that already
// has a linked CLIENT user is never silent — this always names the affected address.
type EmailChangeConfirmation = 'replace' | 'remove' | null;

export const CreateEditClientModal = ({
  title,
  isLoading,
  initialData,
  showNewClientModal,
  toggleNewClientModal,
}: CreateEditClientModalProps) => {
  const history = useHistory();
  const tClients = useTranslation('clients');
  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const createClientMutation = useCreateClient();
  const editClientMutation = useEditClient();
  const removeClientAccessMutation = useRemoveClientAccess();
  const [logoFiles, setLogoFiles] = useState<File[]>([]);
  const [pendingValues, setPendingValues] = useState<FormValues | null>(null);
  const [emailChangeConfirmation, setEmailChangeConfirmation] =
    useState<EmailChangeConfirmation>(null);

  const initialValues = useMemo<FormValues>(() => {
    if (initialData) {
      return {
        name: initialData.name || '',
        address: initialData.address || '',
        phone: initialData.phone || '',
        email: initialData.email || '',
        projectIds:
          (initialData.projectIds && initialData.projectIds.length > 0
            ? initialData.projectIds
            : (initialData.projects || [])
                .map((p) => p.projectId)
                .filter((id): id is string => !!id)) || [],
      };
    }
    return {
      name: '',
      address: '',
      phone: '',
      email: '',
      projectIds: [],
    };
  }, [initialData]);

  // Formik is declared ahead of `submitClient` so its closure over `formik.setFieldError`
  // never forward-references an uninitialized binding (the declaration order matters, so keep
  // it here).
  const formik = useFormik<FormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      address: Yup.string().nullable(),
      phone: Yup.string().nullable(),
      email: Yup.string().email('Invalid email').nullable(),
      projectIds: Yup.array().of(Yup.string()).required('Project is required'),
    }),
    onSubmit: async (values) => {
      const hasLinkedUser = !!initialData?.linkedUser;
      const emailChanged = !!initialData && values.email !== (initialData.email || '');

      // Flow D — never silent: an existing client login is only ever replaced or removed
      // through an explicit confirmation naming the affected address.
      if (hasLinkedUser && emailChanged) {
        setPendingValues(values);
        setEmailChangeConfirmation(values.email ? 'replace' : 'remove');
        return;
      }

      await submitClient(values);
    },
  });

  // Sprint 25, story-128 (run 5). Shared by the plain submit path and both confirmation
  // outcomes (Keep current login / Replace login / Remove access) — see the dispatch below.
  const submitClient = async (values: FormValues): Promise<boolean> => {
    const logoFile = logoFiles.length > 0 ? logoFiles[0] : null;
    formik.setFieldError('email', undefined);
    try {
      if (initialData) {
        await editClientMutation.mutateAsync({
          clientId: initialData.clientId,
          name: values.name,
          address: values.address,
          phone: values.phone,
          email: values.email,
          projectIds: values.projectIds,
          logo: logoFile,
        });
      } else {
        const res = await createClientMutation.mutateAsync({
          name: values.name,
          address: values.address,
          phone: values.phone,
          email: values.email,
          projectIds: values.projectIds,
          logo: logoFile,
        });

        if (res?.clientId) history.push(`/clients/${res.clientId}`);
      }
      toggleNewClientModal();
      return true;
    } catch (error: any) {
      // Sprint 25, story-128 (run 5). "Never partial": the create/update mutation itself
      // already refuses the whole operation server-side on these two errors — this just keeps
      // the modal open and points the user at Email instead of closing on a failed save.
      if (isClientEmailAlreadyMemberError(error)) {
        formik.setFieldError(
          'email',
          (tClients['emailAlreadyMember'] || '{email} is already a member of your organization with a different role.').replace(
            '{email}',
            values.email,
          ),
        );
      } else if (isClientEmailAlreadyLinkedError(error)) {
        formik.setFieldError(
          'email',
          (tClients['emailAlreadyClientUser'] || '{email} is already the client user for another client in this organization.').replace(
            '{email}',
            values.email,
          ),
        );
      }
      return false;
    }
  };

  const handleKeepCurrentLogin = async () => {
    if (!pendingValues) return;
    await submitClient(pendingValues);
    setEmailChangeConfirmation(null);
    setPendingValues(null);
  };

  const handleReplaceLogin = async () => {
    if (!pendingValues || !initialData?.clientId) return;
    // Unlink the old login first so `ClientService.update`'s auto-invite (which only fires when
    // `linkedUserId` is null) picks up the new email on the same save.
    await removeClientAccessMutation.mutateAsync(initialData.clientId);
    await submitClient(pendingValues);
    setEmailChangeConfirmation(null);
    setPendingValues(null);
  };

  const handleRemoveLogin = async () => {
    if (!pendingValues || !initialData?.clientId) return;
    await removeClientAccessMutation.mutateAsync(initialData.clientId);
    await submitClient(pendingValues);
    setEmailChangeConfirmation(null);
    setPendingValues(null);
  };

  const cancelEmailChangeConfirmation = () => {
    setEmailChangeConfirmation(null);
    setPendingValues(null);
  };

  useEffect(() => {
    setLogoFiles([]);
  }, [initialData]);

  const projectsOptions = projects
    .filter((project) => {
      const hasValidId =
        project.projectId !== undefined && project.projectId !== null;
      if (!hasValidId) return false;
      const isUnassigned = !project.clientId;
      const isAssignedToThisClient =
        !!initialData?.clientId && project.clientId === initialData.clientId;
      return isUnassigned || isAssignedToThisClient;
    })
    .map((project) => ({
      value: project.projectId as string,
      label: project.name,
    }));

  const selectedProjectOptions = projectsOptions.filter((opt) =>
    formik.values.projectIds.includes(opt.value as string),
  );

  return (
    <Modal
      title={title}
      cancelText="Cancel"
      isOpen={!!showNewClientModal}
      onClose={toggleNewClientModal}
      handleConfirm={formik.handleSubmit}
      isLoading={
        isLoading ||
        createClientMutation.isPending ||
        editClientMutation.isPending
      }
      confirmText={initialData ? 'Update' : 'Add Client'}
      disableConfirm={isLoading}>
      <form className="flex flex-col gap-6">
        <FileDropZone
          files={logoFiles}
          onFilesChange={(files) => setLogoFiles(files.slice(-1))}
          multiple={false}
          maxFiles={1}
          acceptedTypes="images"
          avatar
          existingImageUrl={initialData?.logo}
          title="Drop logo image here, or click to select"
          subtitle="PNG, JPG, GIF up to 50MB"
        />
        <FormGroup label="Name" htmlFor="name" error={formik.errors.name}>
          <Input
            name="name"
            placeholder="Name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </FormGroup>
        <div className="flex  gap-6">
          <FormGroup label="Email" htmlFor="email" error={formik.errors.email}>
            <Input
              name="email"
              placeholder="Email"
              onBlur={formik.handleBlur}
              value={formik.values.email}
              onChange={formik.handleChange}
            />
          </FormGroup>
          <FormGroup label="Phone" htmlFor="phone" error={formik.errors.phone}>
            <InputPhone
              name="phone"
              country="us"
              placeholder="Phone"
              value={formik.values.phone}
              error={!!formik.errors.phone}
              setFieldValue={(field, value) => {
                formik.setFieldValue(field, value);
                formik.setFieldTouched(field, true, false);
              }}
            />
          </FormGroup>
        </div>
        {/* Sprint 47, story-235: conditional invite notice (replaces story-128's always-on
            hint). Shown for a valid typed email, so the invite is never a surprise; the login
            status line below covers a client that already has a login. */}
        <ClientInviteNotice
          email={formik.values.email}
          savedEmail={initialData?.email}
          hasLinkedUser={!!initialData?.linkedUser}
        />
        {initialData?.linkedUser && (
          <p className="text-xs text-gray-500 -mt-4">
            {tClients['loginStatus']}:{' '}
            {initialData.linkedUser.status === 'ACTIVE'
              ? (tClients['loginStatusActive'] || 'Active').replace(
                  '{date}',
                  formatDateOnly(initialData.linkedUser.lastSignInAt),
                )
              : tClients['loginStatusInvited']}
          </p>
        )}
        <FormGroup
          label="Address"
          htmlFor="address"
          error={formik.errors.address}>
          <GoogleAddressInput
            name="address"
            placeholder="Address"
            value={formik.values.address}
            onChange={(val) => {
              formik.setFieldValue('address', val);
              formik.setFieldTouched('address', true, false);
            }}
            onBlur={formik.handleBlur}
          />
        </FormGroup>
        <FormGroup label="Select Project" htmlFor="project">
          <ReactSelect
            isMulti
            label="Sel  ect Project"
            placeholder="Select Project"
            options={projectsOptions || []}
            value={selectedProjectOptions}
            isDisabled={projectsLoading}
            error={!!formik.errors.projectIds}
            onChange={(selected) => {
              let ids: string[] = [];
              if (Array.isArray(selected)) {
                ids = selected.map((opt) => String(opt.value));
              } else if (selected) {
                const single = selected as SelectOption;
                ids = [String(single.value)];
              }
              formik.setFieldValue('projectIds', ids);
            }}
          />
        </FormGroup>
      </form>

      {/* Sprint 25, story-128 (run 5), Flow D. Replace: "Keep current login" (Email is a
          contact-detail-only change; the existing login is untouched — `submitClient` alone,
          since `linkedUserId` stays set and `ClientService.update` only auto-invites when it's
          null) vs "Replace login" (disable the old membership, invite the new email). Remove:
          the email was cleared entirely. */}
      <ConfirmationModal
        variant="warning"
        isOpen={emailChangeConfirmation === 'replace'}
        onClose={cancelEmailChangeConfirmation}
        onConfirm={handleReplaceLogin}
        title={tClients['replaceLoginTitle'] || 'Replace the client login?'}
        description={(
          tClients['replaceLoginBody'] || '{email} currently has client access. Choose what happens.'
        ).replace('{email}', initialData?.linkedUser?.email || initialData?.email || '')}
        confirmText={tClients['replaceLogin'] || 'Replace login'}
        secondaryText={tClients['keepCurrentLogin'] || 'Keep current login'}
        onSecondary={handleKeepCurrentLogin}
        cancelText="Cancel"
      />
      <ConfirmationModal
        variant="danger"
        isOpen={emailChangeConfirmation === 'remove'}
        onClose={cancelEmailChangeConfirmation}
        onConfirm={handleRemoveLogin}
        title={tClients['removeLoginTitle'] || 'Remove client login?'}
        description={(
          tClients['removeLoginBody'] || '{email} will lose access to these projects. The client record stays.'
        ).replace('{email}', initialData?.linkedUser?.email || initialData?.email || '')}
        confirmText={tClients['removeAccess'] || 'Remove access'}
        cancelText="Cancel"
      />
    </Modal>
  );
};
